import { MODULES } from '@solar/shared';
import type { ModuleKey } from '@solar/types';
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import type { RouteObject } from 'react-router-dom';

import { ModuleGuard } from '@/app/guards';

/**
 * Module routes, generated from the shared registry.
 *
 * Every module gets its path, its permission guard and a page component looked up
 * in `MODULE_PAGES`. Two consequences are the point of doing it this way: a module
 * can never be mounted without its guard, and adding one means adding a registry
 * entry plus a line in the map below — never a change to the router itself.
 *
 * The trailing `/*` on each path lets a module own its nested detail and edit
 * routes in its own route file, rather than growing this one to 120 entries.
 */

type ModulePage = LazyExoticComponent<ComponentType>;

const ModulePlaceholder = lazy(() => import('@/pages/ModulePlaceholder'));
const DashboardRoutes = lazy(() => import('@/pages/dashboards/DashboardRoutes'));
const LeadsRoutes = lazy(() => import('@/pages/leads/LeadsRoutes'));
const CustomersRoutes = lazy(() => import('@/pages/customers/CustomersRoutes'));
const SiteSurveysRoutes = lazy(() => import('@/pages/site-surveys/SiteSurveysRoutes'));
const QuotationsRoutes = lazy(() => import('@/pages/quotations/QuotationsRoutes'));
const ProjectsRoutes = lazy(() => import('@/pages/projects/ProjectsRoutes'));
const ProjectClosuresRoutes = lazy(
  () => import('@/pages/project-closures/ProjectClosuresRoutes'),
);
const WarrantiesRoutes = lazy(() => import('@/pages/warranties/WarrantiesRoutes'));
const ServiceTicketsRoutes = lazy(() => import('@/pages/service-tickets/ServiceTicketsRoutes'));
const ServiceDashboardRoutes = lazy(
  () => import('@/pages/service-tickets/ServiceDashboardRoutes'),
);
const AmcContractsRoutes = lazy(() => import('@/pages/amc-contracts/AmcContractsRoutes'));
const SystemDesignsRoutes = lazy(() => import('@/pages/system-designs/SystemDesignsRoutes'));
const DiscomInspectionsRoutes = lazy(
  () => import('@/pages/discom-inspections/DiscomInspectionsRoutes'),
);
const ElectricalTestsRoutes = lazy(
  () => import('@/pages/electrical-tests/ElectricalTestsRoutes'),
);
const FabricationsRoutes = lazy(() => import('@/pages/fabrications/FabricationsRoutes'));
const InstallationsRoutes = lazy(() => import('@/pages/installations/InstallationsRoutes'));
const NetMetersRoutes = lazy(() => import('@/pages/net-meters/NetMetersRoutes'));
const SubsidiesRoutes = lazy(() => import('@/pages/subsidies/SubsidiesRoutes'));
const MaterialPlansRoutes = lazy(() => import('@/pages/material-plans/MaterialPlansRoutes'));
const CustomerApprovalsRoutes = lazy(
  () => import('@/pages/customer-approvals/CustomerApprovalsRoutes'),
);
const InventoryRoutes = lazy(() => import('@/pages/inventory/InventoryRoutes'));
const StockAllocationsRoutes = lazy(
  () => import('@/pages/stock-allocations/StockAllocationsRoutes'),
);
const StockTransfersRoutes = lazy(() => import('@/pages/stock-transfers/StockTransfersRoutes'));
const StockAdjustmentsRoutes = lazy(
  () => import('@/pages/stock-adjustments/StockAdjustmentsRoutes'),
);

// Purchase. Five documents, one flow: request, order, receive, return, with the
// vendor master they all point at.
const VendorsRoutes = lazy(() => import('@/pages/purchase/vendors/VendorsRoutes'));
const RequisitionsRoutes = lazy(
  () => import('@/pages/purchase/requisitions/RequisitionsRoutes'),
);
const OrdersRoutes = lazy(() => import('@/pages/purchase/orders/OrdersRoutes'));
const GrnRoutes = lazy(() => import('@/pages/purchase/grn/GrnRoutes'));
const ReturnsRoutes = lazy(() => import('@/pages/purchase/returns/ReturnsRoutes'));

// Finance. The overview and the two ageing screens are views; the other four
// are the documents that produce what they show.
const FinanceRoutes = lazy(() => import('@/pages/finance/FinanceRoutes'));
const InvoicesRoutes = lazy(() => import('@/pages/finance/invoices/InvoicesRoutes'));
const PaymentsRoutes = lazy(() => import('@/pages/finance/payments/PaymentsRoutes'));
const ReceivablesRoutes = lazy(() => import('@/pages/finance/receivables/ReceivablesRoutes'));
const PayablesRoutes = lazy(() => import('@/pages/finance/payables/PayablesRoutes'));
const ExpensesRoutes = lazy(() => import('@/pages/finance/expenses/ExpensesRoutes'));
// Integrations. The Tally sync screen is one tab inside this hub now; the
// accounting queue did not change, only where it is reached from.
const IntegrationsRoutes = lazy(() => import('@/pages/integrations/IntegrationsRoutes'));

// The Communication Centre plus the personal notification list. Two modules
// rather than one: what the company tells its customers and what the ERP tells
// you are different data with different readers, and the second needs no
// permission beyond being signed in.
const CommunicationsRoutes = lazy(() => import('@/pages/communications/CommunicationsRoutes'));
const NotificationsPage = lazy(() => import('@/pages/notifications/NotificationsPage'));

// HR. The overview plus six modules, each with its own permission — payroll in
// particular is guarded separately, because seeing who works here and seeing
// what they are paid are not the same right.
const HrRoutes = lazy(() => import('@/pages/hr/HrRoutes'));
const EmployeesRoutes = lazy(() => import('@/pages/hr/employees/EmployeesRoutes'));
const AttendanceRoutes = lazy(() => import('@/pages/hr/attendance/AttendanceRoutes'));
const LeavesRoutes = lazy(() => import('@/pages/hr/leaves/LeavesRoutes'));
const PayrollRoutes = lazy(() => import('@/pages/hr/payroll/PayrollRoutes'));
const HrTasksRoutes = lazy(() => import('@/pages/hr/tasks/TasksRoutes'));
const PerformanceRoutes = lazy(() => import('@/pages/hr/performance/PerformanceRoutes'));

// Reporting. One picker and one runner for all thirteen reports.
const ReportsRoutes = lazy(() => import('@/pages/reports/ReportsRoutes'));

// Administration. Four screens tied together by `AdministrationTabs`, guarded by
// four separate permissions — adding a user, rewriting what a role may do and
// reading the record of who changed what are not the same authority.
const CompanySettingsRoutes = lazy(() => import('@/pages/settings/CompanySettingsRoutes'));
const UsersRoutes = lazy(() => import('@/pages/users/UsersRoutes'));
const RolesRoutes = lazy(() => import('@/pages/roles/RolesRoutes'));
const AuditLogsPage = lazy(() => import('@/pages/audit-logs/AuditLogsPage'));

/**
 * Implemented module pages. Anything absent falls back to `ModulePlaceholder`,
 * which states plainly that the module is not built yet rather than rendering an
 * empty screen that reads as broken.
 *
 * A module that owns nested screens maps to its own `Routes` component — the `/*`
 * on the generated path is what lets it register `new`, `:id` and `:id/edit` itself
 * instead of growing this table to 120 entries.
 */
const MODULE_PAGES: Partial<Record<ModuleKey, ModulePage>> = {
  // The overview plus the seven operational boards at /dashboard/:key.
  dashboard: DashboardRoutes,
  leads: LeadsRoutes,
  customers: CustomersRoutes,
  site_surveys: SiteSurveysRoutes,
  quotations: QuotationsRoutes,
  projects: ProjectsRoutes,
  system_designs: SystemDesignsRoutes,
  customer_approvals: CustomerApprovalsRoutes,
  material_planning: MaterialPlansRoutes,
  fabrication: FabricationsRoutes,
  installation: InstallationsRoutes,
  electrical_testing: ElectricalTestsRoutes,
  net_metering: NetMetersRoutes,
  discom: DiscomInspectionsRoutes,
  subsidy: SubsidiesRoutes,
  project_closure: ProjectClosuresRoutes,
  warranties: WarrantiesRoutes,

  // After-sales nests the way finance, inventory and HR do: /service/* for the
  // overview, /service/tickets/* and /service/amc/* for the screens beneath it.
  // A static segment outranks a splat in React Router, so the children win.
  service_dashboard: ServiceDashboardRoutes,
  service: ServiceTicketsRoutes,
  amc: AmcContractsRoutes,

  // Inventory's three movement documents are separate modules with separate
  // permissions, but their paths nest under /inventory. React Router ranks a
  // static segment above a splat, so `inventory/allocations/*` wins over
  // `inventory/*` and the tab strip makes the seam invisible.
  inventory: InventoryRoutes,
  stock_allocations: StockAllocationsRoutes,
  stock_transfers: StockTransfersRoutes,
  stock_adjustments: StockAdjustmentsRoutes,
  vendors: VendorsRoutes,
  purchase_requisitions: RequisitionsRoutes,
  purchase_orders: OrdersRoutes,
  goods_receipt: GrnRoutes,
  purchase_returns: ReturnsRoutes,

  // Finance nests the same way inventory and purchase do: /finance/* for the
  // overview, /finance/<name>/* for each screen. A static segment outranks a
  // splat in React Router, so the children win and the tab strip hides the seam.
  finance: FinanceRoutes,
  sales_invoices: InvoicesRoutes,
  payments: PaymentsRoutes,
  receivables: ReceivablesRoutes,
  payables: PayablesRoutes,
  expenses: ExpensesRoutes,
  integrations: IntegrationsRoutes,
  communications: CommunicationsRoutes,
  notifications: NotificationsPage,

  // HR nests exactly as finance and inventory do: /hr/* for the overview,
  // /hr/<name>/* for each module. A static segment outranks a splat in React
  // Router, so the children win and the tab strip hides the seam.
  hr: HrRoutes,
  hr_employees: EmployeesRoutes,
  hr_attendance: AttendanceRoutes,
  hr_leaves: LeavesRoutes,
  hr_payroll: PayrollRoutes,
  hr_tasks: HrTasksRoutes,
  hr_performance: PerformanceRoutes,

  reports: ReportsRoutes,

  company_settings: CompanySettingsRoutes,
  users: UsersRoutes,
  roles: RolesRoutes,
  audit_logs: AuditLogsPage,
};

export const moduleRoutes: RouteObject[] = MODULES.map((module) => {
  const Page = MODULE_PAGES[module.key] ?? ModulePlaceholder;

  return {
    path: `${module.path.replace(/^\//, '')}/*`,
    element: (
      <ModuleGuard moduleKey={module.key}>
        <Page />
      </ModuleGuard>
    ),
  };
});

/** Module keys that currently render a real page — used by the placeholder copy. */
export const IMPLEMENTED_MODULES = Object.keys(MODULE_PAGES) as ModuleKey[];
